import AccordionSection from '@app/components/common/Accordion/AccordionSection';
import { IAccordionSection } from '@app/components/common/Accordion/AccordionSection/types';
import Box from '@app/components/common/Box';
import Divider from '@app/components/common/Divider';
import Grid from '@app/components/common/Grid';
import Input from '@app/components/common/Input';
import Switch from '@app/components/common/Switch';
import {
  makeSelectItemData,
  selectIsMenuCreationMode,
} from '@app/state/menu/menuSelectors';
import { displayFlexEndColumnStyles } from '@app/theme/commonStyles';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  SectionId,
  actionCreatorsApp,
  actionCreatorsMenu,
  useFormError,
} from '@westondev/tableturn-core';
import { WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const SECTION_ID = SectionId.BASIC;

const getMemoizedItemData = makeSelectItemData('ingredients', SECTION_ID);

const BasicSection = ({ t, ...props }: IAccordionSection & WithTranslation) => {
  const dispatch = useDispatch();

  const { updateMenuBucket: setValue } = bindActionCreators(
    actionCreatorsMenu,
    dispatch,
  );

  const { showToast } = bindActionCreators(
    actionCreatorsApp,
    dispatch,
  );

  const { itemData } = useSelector(getMemoizedItemData);
  const isCreation = useSelector(selectIsMenuCreationMode);

  const activeSwitch = (
    <Switch
      checked={itemData ? itemData.active : false}
      onChange={active => setValue({ active }, SECTION_ID)}
      showDefaultLabel
    />
  );

  const nameError = useFormError('name');
  const posNameError = useFormError('posName');

  const hasSectionError =
    Boolean(nameError?.error) || Boolean(posNameError?.error);

  return (
    <>
      <AccordionSection
        title={t('loggedIn.commonTitles.basic')}
        required
        hasError={hasSectionError}
        titleTextContainerCsx={{ minWidth: '150px' }}
        collapsedHeaderComponent={
          <Box
            csx={[
              {
                display: 'flex',
                height: '100%',
                gap: '10px',
              },
            ]}>
            <Divider direction="vertical" />
            {activeSwitch}
          </Box>
        }
        optionsDisabled={isCreation}
        onOptionsClickDisabled={() =>
          showToast({
            type: 'info',
            title: t('menuScreen.categoryMain.disabledActionToast.title'),
            description: isCreation
              ? t('menuScreen.categoryDetails.basic.optionsDisabled.creation')
              : t('menuScreen.categoryDetails.basic.optionsDisabled.location'),
          })
        }
        {...props}>
        <>
          {activeSwitch}
          <Divider csx={{ marginBlock: '20px' }} />
          <Grid rowGap={20} columnGap={20} csx={{ alignItems: 'flex-start' }}>
            <Grid.Item
              mb={12}
              sm={6}
              md={6}
              lg={3}
              csx={displayFlexEndColumnStyles}>
              <Input
                required
                caption={nameError?.error}
                error={Boolean(nameError?.error)}
                label={t('loggedIn.commonFields.basic.name')}
                placeholder={t('loggedIn.commonFields.basic.name')}
                value={itemData?.name || ''}
                onChange={name => {
                  setValue(
                    {
                      name: name.target.value,
                    },
                    SECTION_ID,
                  );

                  if (
                    itemData.posName === '' ||
                    itemData.posName === itemData.name
                  ) {
                    setValue(
                      {
                        posName: name.target.value,
                      },
                      SECTION_ID,
                    );
                  }

                  if (itemData.posName === '' && posNameError) {
                    posNameError.clear();
                  }
                }}
                onFocus={() => nameError && nameError.clear()}
              />
            </Grid.Item>
            <Grid.Item
              mb={12}
              sm={6}
              md={6}
              lg={3}
              csx={displayFlexEndColumnStyles}>
              <Box csx={{ display: 'flex' }}>
                <Input
                  required
                  label={t('loggedIn.commonFields.basic.posName')}
                  placeholder={t('loggedIn.commonFields.basic.posName')}
                  caption={posNameError?.error}
                  error={Boolean(posNameError?.error)}
                  value={itemData?.posName || ''}
                  onChange={posName =>
                    setValue(
                      {
                        posName: posName.target.value,
                      },
                      SECTION_ID,
                    )
                  }
                  onFocus={() => posNameError && posNameError.clear()}
                />
              </Box>
            </Grid.Item>
          </Grid>
        </>
      </AccordionSection>
    </>
  );
};

export default BasicSection;
